import React, { useState } from 'react';
import classNames from 'classnames/bind';
import { useInView } from 'react-intersection-observer';
import { IconManualFill, IconPinFill, IconPrivateDeviceFill, IconUnpinFill } from '@saucelabs/ds-icons';
import { Tooltip } from '@saucelabs/ds-tooltip';
import Placeholder from '@saucelabs/sl-images/lib/placeholder.png';

import TrialDiagonal from './TrialDiagonal';
import Styles from './DeviceTile.styles';
import constants from '../constants';
import { useStartTestButtonState } from '../hooks/useStartTestButtonState';

const context = classNames.bind(Styles);

export type DeviceTileProps = {
  readonly email?: string;
  readonly hasSuitableAppConfigurationSelected?: boolean;
  readonly image?: string;
  readonly isCreditsExpired?: boolean;
  readonly isFreemium?: boolean;
  readonly isFreeTrial?: boolean;
  readonly isInUse?: boolean;
  readonly isLocked?: boolean;
  readonly isManualMinutesExpired?: boolean;
  readonly name: string;
  readonly onDetailsClick: () => void;
  readonly onLaunchClick: () => void;
  readonly onPinnedClick: () => void;
  readonly onSelectClick: () => void;
  readonly os: string;
  readonly pinned?: boolean;
  readonly privateDevice?: boolean;
  readonly screenSize: string;
  readonly selected?: boolean;
  readonly unverified?: boolean;
};

const DeviceTile: React.FC<DeviceTileProps> = ({
  email,
  hasSuitableAppConfigurationSelected = true,
  image,
  isCreditsExpired = false,
  isFreemium = false,
  isFreeTrial = false,
  isInUse = false,
  isLocked = false,
  isManualMinutesExpired = false,
  name,
  onDetailsClick,
  onLaunchClick,
  onPinnedClick,
  onSelectClick,
  os,
  pinned = false,
  privateDevice = false,
  screenSize,
  selected = false,
  unverified = false,
}) => {
  const [imageUrlBroken, setImageUrlBroken] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true });
  const { enableAdvise, isButtonDisabled, isDevicePermitted } = useStartTestButtonState({
    email,
    hasSuitableAppConfigurationSelected,
    isCreditsExpired,
    isInUse,
    isLocked,
    isManualMinutesExpired,
    unverified,
  });

  return (
    <div
      className={context(
        'container',
        'bg-white',
        'text-high-emphasis',
        'overflow-hidden',
        { selected },
      )}
      data-qa="device-tile-container"
      onClick={(event) => {
        event.stopPropagation();
        onSelectClick();
      }}
      role="button"
      tabIndex={0}
    >
      {isInUse && (
        <div
          className={Styles['in-use']}
          data-qa="device-in-use"
        >
          in use
        </div>
      )}

      <TrialDiagonal
        isFreemium={isFreemium}
        isFreeTrial={isFreeTrial}
      />

      <div className={context('grid-container', 'p-1', { disabled: isInUse })}>
        <div
          ref={ref}
          className={Styles['image-wrapper']}
        >
          {(!image || !inView) && (
            <img
              alt={name}
              src={Placeholder}
            />
          )}
          {image && inView && (
            <img
              alt={name}
              className={context('image-iphone', { 'filter grayscale opacity-50': isInUse })}
              onError={() => setImageUrlBroken(true)}
              src={imageUrlBroken ? Placeholder : image}
            />
          )}
        </div>

        <div className={Styles['device-name-and-privacy']}>
          <span className={Styles['device-name']}>{name}</span>
          {privateDevice && (
            <Tooltip
              delay={300}
              placement="right"
              title={constants.TOOLTIP_LABELS.PRIVATE_DEVICE}
            >
              <span>
                <IconPrivateDeviceFill className={`${Styles['private-device-icon']} w-2 h-2 fill-current text-green-600`} />
              </span>
            </Tooltip>
          )}
        </div>

        <div className={Styles['device-details']}>
          <div>{os}</div>
          <div>{screenSize}</div>
        </div>

        <div
          className={context('icons-access')}
          data-qa="device-tile-icons-access"
        >
          {!isFreeTrial && (
            <Tooltip
              delay={300}
              placement="top"
              title={pinned ?
                constants.TOOLTIP_LABELS.PINNED_BUTTON_ACTIVE :
                constants.TOOLTIP_LABELS.PINNED_BUTTON_INACTIVE}
            >
              <button
                aria-label={`${pinned ? 'Unpin' : 'Pin'} Device`}
                className={context('button-pinned', 'bg-transparent')}
                data-qa="device-tile-button-pinned"
                onClick={(event) => {
                  event.stopPropagation();
                  onPinnedClick();
                }}
                type="button"
              >
                {pinned ?
                  <IconUnpinFill className="align-middle fill-current h-2 text-blue-700 w-2" /> :
                  <IconPinFill className="align-middle fill-current h-2 text-gray-400 w-2" />}
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      <div
        className={context('bottom-nav', 'bg-blue-700', 'pr-0.5', 'text-white')}
      >
        <button
          className={context('bottom-nav-button', 'button-detail', 'bg-transparent', 'cursor-pointer')}
          data-qa="device-tile-button-detail"
          onClick={(event) => {
            event.stopPropagation();
            onDetailsClick();
          }}
          type="button"
        >
          <span className={Styles['button-text']}>
            Details
          </span>
        </button>

        <Tooltip
          delay={300}
          isOpen={!isDevicePermitted ? undefined : false}
          placement="top"
          title={enableAdvise}
        >
          <div>
            <button
              className={context('bottom-nav-button', 'bg-transparent', 'cursor-pointer', 'relative', {
                disabled: isButtonDisabled,
              })}
              data-qa="device-tile-button-start-test"
              disabled={isButtonDisabled}
              onClick={(event) => {
                event.stopPropagation();
                onLaunchClick();
              }}
              type="button"
            >
              <IconManualFill className={`${Styles['bottom-nav-icons']} fill-current`} />
              <span className={Styles['button-text']}>
                Start Test
              </span>
            </button>
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default DeviceTile;
